// A minimal dialog on the estate tokens — replaces Mantine's Modal at the
// ported call sites (ADR-0010). Escape and the backdrop both close it; the
// content owns its own buttons.
import { X } from 'lucide-react'
import { type ReactNode, useEffect } from 'react'

export function Modal({ opened, onClose, title, children, wide = false }: {
  opened: boolean
  onClose: () => void
  title: ReactNode
  children: ReactNode
  wide?: boolean
}) {
  useEffect(() => {
    if (!opened) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [opened, onClose])

  if (!opened) return null
  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4 pt-16"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className={`w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} rounded-lg border border-border bg-background p-4 shadow-xl`}
        role="dialog"
        aria-modal="true"
      >
        <div className="mb-3 flex items-center justify-between gap-4">
          <h2 className="text-base font-semibold text-foreground">{title}</h2>
          <button
            className="rounded p-1 text-muted-foreground hover:bg-muted"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

/** The estate's small button vocabulary, so ported screens speak one dialect. */
export function Btn({
  children,
  onClick,
  kind = 'default',
  size = 'sm',
  disabled = false,
  loading = false,
  type = 'button',
  title,
  className = '',
}: {
  children: ReactNode
  onClick?: () => void
  kind?: 'primary' | 'default' | 'danger' | 'subtle' | 'warning'
  size?: 'xs' | 'sm' | 'md'
  disabled?: boolean
  loading?: boolean
  type?: 'button' | 'submit'
  title?: string
  className?: string
}) {
  const kinds = {
    primary: 'bg-primary text-primary-foreground hover:opacity-90',
    default: 'border border-border hover:bg-muted',
    danger: 'bg-red-600 text-white hover:bg-red-700',
    warning: 'bg-amber-500 text-white hover:bg-amber-600',
    subtle: 'text-muted-foreground hover:bg-muted',
  }
  const sizes = {
    xs: 'px-2 py-1 text-xs',
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
  }
  return (
    <button
      type={type}
      title={title}
      className={`inline-flex items-center justify-center gap-1.5 rounded-md font-medium disabled:cursor-not-allowed disabled:opacity-50 ${kinds[kind]} ${sizes[size]} ${className}`}
      disabled={disabled || loading}
      onClick={onClick}
    >
      {loading ? '…' : children}
    </button>
  )
}
